import * as Phaser from "phaser-ce"
import { FontType } from "../../../../common/TextUtils"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { TransitionState } from "../../../GameState"
import { Buttons } from "../../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserText } from "../../../utils/PhaserText"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Transitions } from "../../transitions/Transitions"
import { SecondHandRepo } from "../SecondHandRepo"
import { loadAssets, SecondHandAssets } from "./SecondHandAssets"
import { ShPos } from "./ShPos"

export class SecondHandState extends Phaser.State {

    mouse: ExpandoSprite
    lungs: ExpandoSprite
    vaporExperiment: ExpandoSprite
    shortTermSmokingExperiment: ExpandoSprite
    table: Phaser.Image
    madScientist: ScientistSprite

    private readonly introWords: Array<Word> = [
        new Word(30, 342, [Sound.E, Sound.O], "Hello"),
        new Word(731, 1048, [Sound.A, Sound.L], "and wel-"),
        new Word(1115, 1683, [Sound.CON, Sound.MBP, Sound.A, Sound.MBP], "come to my lab."),
        new Word(2525, 2842, [Sound.O, Sound.A], "Today"),
        new Word(3036, 3405, [Sound.E, Sound.A, Sound.O], "we are going"),
        new Word(3527, 3701, [Sound.O, Sound.CON], "to dis-"),
        new Word(3747, 4000, [Sound.CON, Sound.O, Sound.CON], "cover"),
        new Word(4075, 4659, [Sound.CON, Sound.E, Sound.CON], "the science"),
        new Word(4700, 4960, [Sound.FV, Sound.E, Sound.CON], "of second"),
        new Word(5016, 5689, [Sound.A, Sound.CON, Sound.O], "hand smoke"),
        new Word(6136, 7253, [Sound.A, Sound.E, Sound.A, Sound.FV, Sound.A, Sound.CON], "and secondhand vapor."),

        new Word(8272, 9175, [Sound.E, Sound.CON, Sound.O], "Secondhand smoke"),
        new Word(9230, 9474, [Sound.CON], "is the"),
        new Word(9558, 10011, [Sound.CON, Sound.MBP, Sound.U], "term used"),
        new Word(10069, 10374, [Sound.O, Sound.CON], "to desc-"),
        new Word(10458, 10700, [Sound.CON], "cribe"),
        new Word(10789, 11386, [Sound.A, Sound.MBP, Sound.CON], "what happens."),
        new Word(11453, 12385, [Sound.E, Sound.O, Sound.CON], "when nonsmokers"),
        new Word(12500, 13273, [Sound.CON, Sound.O], "breathe in the smoke"),
        new Word(13659, 14109, [Sound.FV, Sound.O, Sound.CON], "from other"),
        new Word(14187, 15100, [Sound.E, Sound.L, Sound.CON, Sound.A, Sound.CON], "people's cigarettes."),

        new Word(15967, 16852, [Sound.E, Sound.A, Sound.FV, Sound.CON], "Secondhand vapor"),
        new Word(17250, 17750, [Sound.E, Sound.CON, Sound.CON], "describes"),
        new Word(17853, 18509, [Sound.E, Sound.CON, Sound.FV], "breathing in vap-"),
        new Word(18579, 19500, [Sound.O, Sound.MBP, Sound.O, Sound.E, Sound.CON], "or from someone else's"),
        new Word(19566, 20129, [Sound.E, Sound.CON, Sound.A, Sound.CON], "e-cigarette."),

        new Word(21200, 21849, [Sound.CON, Sound.O, Sound.CON, Sound.E], "Click on the ex-"),
        new Word(21850, 22729, [Sound.MBP, Sound.E, Sound.CON, Sound.L, Sound.A], "periments in this lab"),
        new Word(23211, 23750, [Sound.O, Sound.FV, Sound.CON], "to find out the"),
        new Word(23856, 24275, [Sound.A, Sound.MBP], "damage"),
        new Word(24352, 25240, [Sound.A, Sound.CON, Sound.O], "that secondhand smoke"),
        new Word(25655, 26613, [Sound.A, Sound.E, Sound.CON, Sound.FV, Sound.A, Sound.CON], "and secondhand vapor"),
        new Word(26929, 27452, [Sound.A, Sound.CON], "can cause."),
    ]

    private readonly postExperimentWords: Array<Word> = [
        new Word(291, 579, [Sound.O, Sound.CON], "Now that"),
        new Word(650, 750, [Sound.U, Sound.FV], "you've"),
        new Word(850, 1077, [Sound.FV, Sound.CON], "finished"),
        new Word(1155, 1913, [Sound.CON, Sound.E, Sound.MBP, Sound.E, Sound.CON], "this experiment"),
        new Word(2385, 2500, [Sound.CON], "check"),
        new Word(2563, 2680, [Sound.O], "out"),
        new Word(2773, 3271, [Sound.CON, Sound.E, Sound.CON], "the rest."),
    ]

    private readonly doneWords: Array<Word> = [
        new Word(275, 635, [Sound.A, Sound.CON, Sound.O], "Thanks for"),
        new Word(685, 900, [Sound.O, Sound.CON], "coming"),
        new Word(950, 1410, [Sound.O, Sound.L, Sound.A], "to my lab"),
        new Word(1650, 2080, [Sound.O, Sound.L, Sound.CON, Sound.A, Sound.MBP], "to learn about"),
        new Word(2155, 2765, [Sound.CON, Sound.A, Sound.FV], "the dangers of"),
        new Word(2800, 3630, [Sound.E, Sound.CON, Sound.A, Sound.O, Sound.CON], "secondhand smoke"),
        new Word(3813, 4333, [Sound.A, Sound.FV, Sound.A, Sound.CON], "and vapor."),

        new Word(5090, 5161, [Sound.U], "You"),
        new Word(5214, 5466, [Sound.A, Sound.E], "can see"),
        new Word(5526, 5781, [Sound.A, Sound.FV], "any of"),
        new Word(5849, 5947, [Sound.CON], "the"),
        new Word(5948, 6808, [Sound.E, Sound.MBP, Sound.E, Sound.CON, Sound.A, Sound.CON], "experiments again"),
        new Word(7272, 7365, [Sound.O], "or"),
        new Word(7452, 7587, [Sound.CON], "click"),
        new Word(7738, 8000, [Sound.E, Sound.CON], "exit."),
    ]

    preload() {
        loadAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setGameBackgroundColorGreen(this.game)

        let audio: string
        let words: Array<Word>
        if (this.allExperimentsComplete()) {
            audio = SecondHandAssets.EXIT
            words = this.doneWords
        } else if (this.anyExperimentComplete()) {
            audio = SecondHandAssets.CHECKOUT_THE_REST
            words = this.postExperimentWords
        } else {
            audio = SecondHandAssets.INTRO
            words = this.introWords
        }

        const fullDesk = this.game.add.image(0, ShPos.fullDeskY(), SecondHandAssets.FULL_DESK)
        Scale.scaleSprite(fullDesk, 0.5)
        fullDesk.x = Scale.width() - fullDesk.width

        const skeleton = this.game.add.image(ShPos.skeletonX(), ShPos.skeletonY(), SecondHandAssets.SKELETON)
        Scale.scaleSprite(skeleton, 1.2)

        this.madScientist = new ScientistSprite(this.game, ShPos.scientistX(), ShPos.scientistY(), audio, 0.79, words)
        this.madScientist.audio.onStop.add(() => {
            this.turnExperimentsOn()
        })

        this.table = this.game.add.image(0, 0, SecondHandAssets.TABLE)
        Scale.fillScreenWidth(this.table)

        if (this.allExperimentsComplete()) {
            const backToScienceMuseum = PhaserText.create(this.game, "Exit", {
                x: ShPos.backToMuseumX(), y: ShPos.backToMuseumY(), fontPx: 36, type: FontType.TITLE
            })
            backToScienceMuseum.events.onInputDown.add(() => {
                Transitions.startOutro(this.game, ActivityKey.SECOND_HAND)
                SecondHandRepo.destory()
                this.madScientist.audio.stop()
            })
            this.madScientist.audio.onStop.add(() => Buttons.makeSpriteButton(backToScienceMuseum))
        }

        const experimentScale = 0.75

        const secondExpandoFactory = ExpandoSprite.expandoFactory(this.game, experimentScale, false)

        this.shortTermSmokingExperiment =
            secondExpandoFactory(ShPos.shortTermSmokingExperimentX(), ShPos.shortTermSmokingExperimentY(), SecondHandAssets.SHORT_TERM_SMOKING_EXPERIMENT)
        this.shortTermSmokingExperiment.addOnClick(() => {
            this.game.state.start(TransitionState.SHORT_TERM)
        })
        this.shortTermSmokingExperiment.addTitle("Short Term Effects of\nSecondhand Smoke",
            {x: ShPos.shortTermSmokingExperimentTitleX(), y: ShPos.shortTermSmokingExperimentTitleY(), linesHigh: 2, fontType: FontType.SILLY_TITLE})

        this.vaporExperiment = secondExpandoFactory(ShPos.shortTermVaporExperimentX(), ShPos.shortTermVaporExperimentY(), SecondHandAssets.VAPOR_EXPERIMENT)
        this.vaporExperiment.addOnClick(() => {
            this.game.state.start(TransitionState.EFFECTS_OF_VAPOR)
        })
        this.vaporExperiment.addTitle("Effects of\nSecondhand Vapor", {
            x: ShPos.shortTermVaporExperimentTitleX(),
            y: ShPos.shortTermVaporExperimentTitleY(), linesHigh: 2, fontType: FontType.SILLY_TITLE
        })

        this.mouse = secondExpandoFactory(ShPos.mouseX(), ShPos.mouseY(), SecondHandAssets.LONG_TERM_SMOKING)
        this.mouse.addOnClick(() => {
            this.game.state.start(TransitionState.LONG_TERM)
        })
        this.mouse.addTitle("Long Term Effects of\nSecondhand Smoke", {
            x: ShPos.mouseTitleX(), y: ShPos.mouseTitleY(), linesHigh: 2,
            fontType: FontType.SILLY_TITLE
        })

        this.lungs = secondExpandoFactory(ShPos.lungsX(), ShPos.lungsY(), SecondHandAssets.LUNGS)
        this.lungs.addOnClick(() => {
            this.game.state.start(TransitionState.SECOND_VS_DIRECT)
        })
        this.lungs.addTitle("Direct vs. Secondhand\nSmoke and Vapor", {
            x: ShPos.lungsTitleX(), y: ShPos.lungsTitleY(), linesHigh: 2,
            fontType: FontType.SILLY_TITLE
        })

        this.grayOutCompleted()
    }

    grayOutCompleted = () => {
        if (SecondHandRepo.instance.isLongTermComplete()) {
            this.mouse.grayOut()
        }
        if (SecondHandRepo.instance.isShortTermComplete()) {
            this.shortTermSmokingExperiment.grayOut()
        }
        if (SecondHandRepo.instance.isVaporComplete()) {
            this.vaporExperiment.grayOut()
        }
        if (SecondHandRepo.instance.isFirstHandVsSecondHandComplete()) {
            this.lungs.grayOut()
        }
    }

    private turnExperimentsOn = () => {
        this.lungs.on = true
        this.mouse.on = true
        this.vaporExperiment.on = true
        this.shortTermSmokingExperiment.on = true
    }

    private anyExperimentComplete = (): boolean => {
        return SecondHandRepo.instance.isLongTermComplete() || SecondHandRepo.instance.isShortTermComplete() ||
            SecondHandRepo.instance.isVaporComplete() || SecondHandRepo.instance.isFirstHandVsSecondHandComplete()
    }

    private allExperimentsComplete = (): boolean => {
        return SecondHandRepo.instance.isLongTermComplete() && SecondHandRepo.instance.isShortTermComplete()
            && SecondHandRepo.instance.isVaporComplete() && SecondHandRepo.instance.isFirstHandVsSecondHandComplete()
    }

}
